import { useQuery, useMutation, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import api from '../lib/api';
import {
  Notification,
  NotificationsListResponse,
  NotificationsResult,
  NotificationResponse,
  NotificationsQueryParams,
} from '../types/notification';

/**
 * List notifications
 * GET /api/v1/notifications
 *
 * The backend nests the list under `data.notifications` (alongside
 * `unread_count` and `pagination`), so we unwrap that envelope here and hand
 * components a flat, predictable shape.
 */
export const fetchNotifications = async (
  params: NotificationsQueryParams = {}
): Promise<NotificationsResult> => {
  const queryParams = new URLSearchParams();

  if (params.read !== undefined) queryParams.append('read', params.read.toString());
  if (params.type) queryParams.append('type', params.type);
  if (params.page) queryParams.append('page', params.page.toString());
  if (params.limit) queryParams.append('limit', params.limit.toString());

  const response = await api.get<NotificationsListResponse>(
    `/notifications?${queryParams.toString()}`
  );
  const payload = response.data.data;
  return {
    notifications: payload?.notifications ?? [],
    unreadCount: payload?.unread_count ?? 0,
    pagination: payload?.pagination,
  };
};

export const useNotifications = (
  params: NotificationsQueryParams = {}
): UseQueryResult<NotificationsResult, Error> => {
  return useQuery({
    queryKey: ['notifications', params],
    queryFn: () => fetchNotifications(params),
    staleTime: 30 * 1000, // 30 seconds - notifications should be fresh
    gcTime: 5 * 60 * 1000,
  });
};

/**
 * Mark notification as read
 * POST /api/v1/notifications/:id/read
 */
export const markNotificationRead = async (
  notificationId: string
): Promise<Notification> => {
  const response = await api.post<NotificationResponse>(
    `/notifications/${notificationId}/read`
  );
  return response.data.data;
};

export const useMarkNotificationRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: markNotificationRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
};

/**
 * Mark every notification as read
 * POST /api/v1/notifications/read-all
 */
export const markAllNotificationsRead = async (): Promise<number> => {
  const response = await api.post<{ data: { updated: number } }>('/notifications/read-all');
  return response.data.data.updated;
};

export const useMarkAllNotificationsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
};

/**
 * Unread count as the user SEES it: chat messages collapse into one row per
 * sender in the panel, so the badge must count grouped rows, not raw
 * notifications — a raw server count reads as "wrong" (e.g. badge 10, list 6).
 */
export const countUnreadGroups = (notifications: Notification[]): number => {
  const chatSenders = new Set<string>();
  let count = 0;
  for (const n of notifications) {
    if (n.read) continue;
    if (n.type === 'chat_message') {
      chatSenders.add(n.title);
    } else {
      count += 1;
    }
  }
  return count + chatSenders.size;
};

/**
 * Delete a notification
 * DELETE /api/v1/notifications/:id
 */
export const deleteNotification = async (id: string): Promise<void> => {
  await api.delete(`/notifications/${id}`);
};

export const useDeleteNotification = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteNotification,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
};
